import { useCallback, useState } from 'react';

import type { GridRowParams } from '@mui/x-data-grid';

import type { ResolverReturnTypeId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard.hook';
import type { ReferencesContext_Resolver } from '../../contexts/ReferencesContext';

import { DataGrid } from '../DataGrid';
import { Loading } from '../Loading';

import { getColumns } from './get-columns';
import { useInitialGridState } from './hooks/useInitialGridState.hook';
import type {
  OnSelectHandler,
  OnSelectHandlerValueType,
} from './types/on-select-handler.type';
import type { ResolversSettings } from './types/resolvers-settings.type';

interface Props {
  readonly onSelect?: OnSelectHandler;
  readonly resolversSettings: ResolversSettings;
}

export const ResolverList = ({
  onSelect: handleSelect,
  resolversSettings: {
    allowedReturnTypeIds,
    enableDirectGeneration,
    mustBeResolvableOnBack,
    mustBeResolvableOnFront,
  },
}: Readonly<Props>) => {
  const [isLoading, setIsLoading] = useState(false);

  const { copyToClipboard } = useCopyToClipboard();

  const copy = useCallback<OnSelectHandler>(
    (_: OnSelectHandlerValueType, value: string) => {
      copyToClipboard(value);
    },
    [copyToClipboard],
  );

  const { resolvers, resolverGroups, resolverReturnTypes } =
    useReferencesContext();

  const enableActions = ({
    row,
  }: Readonly<GridRowParams<ReferencesContext_Resolver>>) =>
    (!allowedReturnTypeIds ||
      allowedReturnTypeIds.includes(
        row.returnTypeId as ResolverReturnTypeId,
      )) &&
    (!mustBeResolvableOnBack || row.isResolvableOnBack) &&
    (!mustBeResolvableOnFront || row.isResolvableOnFront);

  const { initialGridState } = useInitialGridState({
    allowedReturnTypeIds,
    mustBeResolvableOnBack,
    mustBeResolvableOnFront,
  });

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!isLoading && <Loading />}
      <DataGrid
        columns={getColumns({
          resolverGroups,
          resolverReturnTypes,
          enableActions,
          enableDirectGeneration,
          handleSelect: handleSelect ?? copy,
          setIsLoading,
        })}
        initialState={initialGridState}
        rows={resolvers}
      />
    </>
  );
};
